/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type SetStateAction, useEffect } from "react";

import type { SoilSightProjectResponse, SoilSightScenario, SoilSightScenarioDetail } from "models";

import { type ProjectRefinementsState, type ScenarioData, type ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { type AutosaveRefs, buildInitialState, seedAutosaveRefsFromHydration } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type HydrationArgs = {
  soilSightProjectId: string | null;
  allDetailsResolved: boolean;
  projectQuery: { isSuccess: boolean; data?: SoilSightProjectResponse };
  scenarioListQuery: { isSuccess: boolean; data?: SoilSightScenario[] };
  scenarioDetails: SoilSightScenarioDetail[];
  /** Seeded from the hydrated data, so the autosaves skip what the server already has. */
  autosaveRefs: AutosaveRefs;
  setSettings: Dispatch<SetStateAction<ScenarioSettings>>;
  setProjectRefinements: Dispatch<SetStateAction<ProjectRefinementsState>>;
  setScenarios: Dispatch<SetStateAction<ScenarioData[]>>;
  setHasCalculated: Dispatch<SetStateAction<boolean>>;
  setHydrationApplied: Dispatch<SetStateAction<boolean>>;
  /** When true, crop years were saved by a field import just before navigation, so no calculation exists yet. */
  importAlreadyPersisted?: boolean;
  /** When provided, overrides the farm refinement selected field after hydration from the server. */
  importedSelectedFieldId?: string | null;
};

/** One-shot hydration when farm + list + per-scenario details are ready. */
export const useSoilSightHydration = (args: HydrationArgs): void => {
  const {
    soilSightProjectId,
    allDetailsResolved,
    projectQuery,
    scenarioListQuery,
    scenarioDetails,
    autosaveRefs,
    setSettings,
    setProjectRefinements,
    setScenarios,
    setHasCalculated,
    setHydrationApplied,
    importAlreadyPersisted,
    importedSelectedFieldId,
  } = args;
  const { hasHydratedRef, cropYearsSavedRef, scenarioMetaSavedRef, settingsSavedRef } = autosaveRefs;

  useEffect(
    function hydrateFromServerOnce() {
      if (hasHydratedRef.current) return;
      if (!soilSightProjectId) return;
      if (!projectQuery.isSuccess || !scenarioListQuery.isSuccess) return;
      if (!allDetailsResolved) return;

      const summaries = scenarioListQuery.data ?? [];
      const { hydrated, projectRefinements, scenarios, hasCalculated } = buildInitialState({
        projectData: projectQuery.data,
        summaries,
        scenarioDetails,
        importedSelectedFieldId,
        importAlreadyPersisted,
      });

      setSettings(hydrated.settings);
      setProjectRefinements(projectRefinements);
      if (summaries.length > 0) {
        setScenarios(scenarios);
        if (hasCalculated) setHasCalculated(true);
      }

      hasHydratedRef.current = true;
      seedAutosaveRefsFromHydration({ hydrated, hydratedScenarios: scenarios, summaries, cropYearsSavedRef, scenarioMetaSavedRef, settingsSavedRef });
      setHydrationApplied(true);
    },
    [
      allDetailsResolved,
      cropYearsSavedRef,
      projectQuery.data,
      projectQuery.isSuccess,
      hasHydratedRef,
      scenarioDetails,
      scenarioListQuery.data,
      scenarioListQuery.isSuccess,
      scenarioMetaSavedRef,
      settingsSavedRef,
      setProjectRefinements,
      setHasCalculated,
      setHydrationApplied,
      setScenarios,
      setSettings,
      soilSightProjectId,
      importAlreadyPersisted,
      importedSelectedFieldId,
    ]
  );
};
